// ============================================================
// v7 — the Card Codex content (shared). The How-to-Play page joins
// each CARD_CATALOG entry to a guide entry here: the long "how it
// works" prose, the mechanics chips, and a demo descriptor.
//
// Single source of truth: labels/short descriptions come from
// CARD_CATALOG (never duplicated); the mechanics numbers here are
// DERIVED from DEFAULT_SETTINGS / SEEKER_COST / the radii tables, so
// the codex can never drift from the server defaults (full spec §4
// note, v7 §6). A completeness test asserts every catalog id has an
// entry here — adding a card without a codex entry fails CI.
// ============================================================
import { DEFAULT_SETTINGS, RADAR_RADII_FT, THERMO_MINS_FT } from './constants';
import { formatDurationMs } from './duration';
import { SEEKER_COST } from './hiderdeck';

/** The set of map/concept demos `CardDemo` knows how to render. The union is the
 *  authority; `CardDemo` must handle every member (asserted by a registry test). */
export type DemoType = 'radar' | 'direction' | 'quadrants' | 'borders' | 'thermometer' | 'photo' | 'freeze' | 'silence' | 'babel' | 'flying-blind' | 'portal' | 'bonus' | 'veto' | 'airhorn' | 'landmark'; // v19: per-curse demos replace the generic 'curse-time'

export const DEMO_TYPES: readonly DemoType[] = [
  'radar',
  'direction',
  'quadrants', // v11 §2K
  'borders', // v11 §2L
  'thermometer',
  'photo',
  'freeze', // v19: each timed curse has its own accurate demo now
  'silence',
  'babel',
  'flying-blind',
  'portal',
  'bonus',
  'veto',
  'airhorn', // v16 BOLT-072
  'landmark', // v24 BOLT-118: the map-data cards
] as const;

export interface DemoDescriptor {
  type: DemoType;
  /** radius (ft) / distance (ft) / bonus amount / curse label — display only */
  param?: number | string;
  /** one-line narration under the demo stage */
  caption: string;
}

export interface CardGuideMechanics {
  cost?: string; // seeker: hider cards dealt on resolution
  window?: string; // a decision/upload/walk window
  duration?: string; // a curse block length
  mapImpact: string; // what it does to the map (or "No map effect")
}

export interface CardGuideEntry {
  how: string; // the long, plain-language "how it works"
  mechanics: CardGuideMechanics;
  demo: DemoDescriptor;
}

// ---- derived, never-stale strings ----
const S = DEFAULT_SETTINGS;
const secs = (ms: number) => `${Math.round(ms / 1000)} s`;
// v15 BOLT-065: ONE duration renderer everywhere (shared/duration.ts).
const dur = formatDurationMs;
const cost = (id: string) => {
  const n = SEEKER_COST[id] ?? 0;
  return `${n} hider card${n === 1 ? '' : 's'}`;
};
const vetoWin = `${secs(S.vetoWindowMs)} veto window`;
const photoWin = dur(S.photoWindowMs);
const thermoWin = dur(S.thermoWindowMs);
const penaltyFt = `${S.photoPenaltyRadiusFt} ft`;

const radarHow = (ft: number) =>
  `Asks: is the hider within ${ft} ft of you? You get INSIDE or OUTSIDE, and the "possible area" is clipped to match: kept inside the ring if yes, outside it if no. A smaller ring is a sharper answer and costs the hider more cards.`;

export const CARD_GUIDE: Record<string, CardGuideEntry> = {
  // ============ hider deck (8 types) ============
  bonus2: {
    how: 'Banks +2 minutes onto your hiding time, with a cha-ching so you feel the deposit land. Banked bonuses are added to your survival time when you are found. More banked time, better score. You draw cards as the seekers play, so the more they play, the more time you can stockpile.',
    mechanics: { mapImpact: 'No map effect' },
    demo: { type: 'bonus', param: 2, caption: '+2 min → banked' },
  },
  bonus5: {
    how: 'Banks +5 minutes onto your hiding time. The same as +2, just larger.',
    mechanics: { mapImpact: 'No map effect' },
    demo: { type: 'bonus', param: 5, caption: '+5 min → banked' },
  },
  bonus10: {
    how: 'Banks +10 minutes onto your hiding time. The big flat bonus.',
    mechanics: { mapImpact: 'No map effect' },
    demo: { type: 'bonus', param: 10, caption: '+10 min → banked' },
  },
  veto: {
    how: `Cancels a seeker card before it resolves. When the seekers play a card there is a short delay (default ${secs(
      S.vetoWindowMs,
    )}); tap Veto before it ends to kill the card. It is wasted and reveals nothing. Veto is spent from your hand (you do not draw a card for it).`,
    mechanics: { window: vetoWin, mapImpact: 'Cancels a card. No map change' },
    demo: { type: 'veto', caption: 'Veto in time → card struck out' },
  },
  // v15 BOLT-054: the two curses split cleanly — "I either can't move or
  // can't play cards." BOLT-065: the numbers below derive from
  // DEFAULT_SETTINGS and are LABELED as defaults (a host can change them).
  holdup: {
    how: `The movement freeze: the seekers must physically STOP where they stand until it clears, but their cards stay live. They can keep playing from the spot. Honor system, like standing still in tag. A STOP sign and countdown show on the seeker's screen.`,
    mechanics: { duration: `${dur(S.blockDurationMs)} by default. The host can change this in Game settings.`, mapImpact: 'No map effect. A physical freeze' },
    demo: { type: 'freeze', caption: `Freeze → seekers stop where they stand (cards still allowed)` }, // v19: movement, not cards
  },
  snail: {
    how: `The card block: the seekers cannot play ANY card until it clears, but they can keep moving freely. A countdown shows on both teams' screens.`,
    mechanics: { duration: `${dur(S.blockDurationMs)} by default. The host can change this in Game settings.`, mapImpact: 'Blocks all seeker cards' },
    demo: { type: 'silence', caption: `Silence → seekers can't play any card (they can still move)` },
  },
  babel: {
    // v16 BOLT-071 — capability-gated to protocol-v6 rooms (older phones
    // never see the card). Numberless per the BOLT-065 law.
    how: `The tongue-twister: for its duration the SEEKER's whole round screen renders in another language: Japanese, Chinese, French, or Swahili, rolled at cast time. They can still play; they just have to play in translation. You (the hider) stay readable. It clears on its own.`,
    mechanics: { duration: `${dur(S.blockDurationMs)} by default. The host can change this in Game settings.`, mapImpact: 'No map effect. A language scramble' },
    demo: { type: 'babel', caption: `Babel → the seeker's card labels change language` },
  },
  flyingBlind: {
    // v18 BOLT-080 — capability-gated to protocol-v7 rooms. Numberless per the
    // BOLT-065 law: the duration comes from live settings, not this prose.
    how: `The blackout: for its duration the SEEKER's map loses its tiles. Streets, labels, water, parks all go dark, and the only things left are the play area outline, the shrinking possible area, and their own pin. They can still move and still play cards. They just cannot read the streets while they do it.`,
    mechanics: { duration: `${dur(S.blockDurationMs)} by default. The host can change this in Game settings.`, mapImpact: 'No map effect. The seeker loses their basemap' },
    demo: { type: 'flying-blind', caption: `Flying Blind → the seeker's map tiles go dark (area + zone stay)` },
  },
  portal: {
    how: 'Drop a hidden teleport trap on the map. You secretly place an entry box and an exit box on roads. The seekers never see them. When a seeker walks into the entry box they are teleported: their pin jumps to the exit box and they cannot play cards until they physically walk to the exit and step out. A nasty detour that buys you time.',
    mechanics: {
      mapImpact: `Box ${S.portalSizeFt} ft · exit ${S.portalMinExitFt}–${S.portalMaxExitFt} ft away · ${S.portalKeepoutFt} ft keepout · no cards until cleared`,
    },
    demo: { type: 'portal', caption: 'Step on the trap → teleported to the exit' },
  },

  // ============ seeker deck (21 cards) ============
  // Radius family (wire ids/family stay "radar")
  radar1000: {
    how: radarHow(RADAR_RADII_FT[0]),
    mechanics: { cost: cost('radar1000'), window: vetoWin, mapImpact: `Clips the zone to / from a ${RADAR_RADII_FT[0]} ft disk` },
    demo: { type: 'radar', param: RADAR_RADII_FT[0], caption: `Within ${RADAR_RADII_FT[0]} ft? → zone clipped` },
  },
  radar500: {
    how: radarHow(RADAR_RADII_FT[1]),
    mechanics: { cost: cost('radar500'), window: vetoWin, mapImpact: `Clips the zone to / from a ${RADAR_RADII_FT[1]} ft disk` },
    demo: { type: 'radar', param: RADAR_RADII_FT[1], caption: `Within ${RADAR_RADII_FT[1]} ft? → zone clipped` },
  },
  radar250: {
    how: radarHow(RADAR_RADII_FT[2]),
    mechanics: { cost: cost('radar250'), window: vetoWin, mapImpact: `Clips the zone to / from a ${RADAR_RADII_FT[2]} ft disk` },
    demo: { type: 'radar', param: RADAR_RADII_FT[2], caption: `Within ${RADAR_RADII_FT[2]} ft? → zone clipped` },
  },
  radar100: {
    how: radarHow(RADAR_RADII_FT[3]) + ' At 100 ft you are nearly on top of them.',
    mechanics: { cost: cost('radar100'), window: vetoWin, mapImpact: `Clips the zone to / from a ${RADAR_RADII_FT[3]} ft disk` },
    demo: { type: 'radar', param: RADAR_RADII_FT[3], caption: `Within ${RADAR_RADII_FT[3]} ft? → zone clipped` },
  },
  radar1km: {
    how: 'Asks: is the hider within 1 km of you? You get INSIDE or OUTSIDE and the possible area is clipped to match. A long-range ring, most useful early, or on a big boundary, to lop off whole sides of the map; on a small boundary it may answer INSIDE and reveal little. The widest rings are the weakest cuts, so they cost the hider the least.',
    mechanics: { cost: cost('radar1km'), window: vetoWin, mapImpact: 'Clips the zone to / from a 1 km disk' },
    demo: { type: 'radar', param: 3281, caption: 'Within 1 km? → zone clipped' },
  },
  radar5km: {
    how: 'Asks: is the hider within 5 km of you? The longest-range radar. It blankets most play areas, so on a typical boundary it usually answers INSIDE and clips almost nothing. It earns its keep only on very large play areas, where it can still cut a far edge. The weakest possible cut, so it costs the hider the least.',
    mechanics: { cost: cost('radar5km'), window: vetoWin, mapImpact: 'Clips the zone to / from a 5 km disk' },
    demo: { type: 'radar', param: 16404, caption: 'Within 5 km? → zone clipped' },
  },
  direction: {
    // v11 §2K copy fix: Direction cuts DIAGONAL-edged wedges (an X through
    // you), NOT axis-aligned map quadrants — that's the Quadrants card.
    how: 'Tells you which diagonal 90° wedge (N/E/S/W of an X centered on you) the hider is in, and clips the possible area to that wedge. The wedge edges run diagonally (northeast, southeast, and so on), not along the map grid. One of the strongest single cuts when you are roughly centered on the zone.',
    mechanics: { cost: cost('direction'), window: vetoWin, mapImpact: 'Clips the zone to a diagonal-edged 90° wedge (X-cut)' },
    demo: { type: 'direction', caption: 'Which X-wedge? → zone clipped to the diagonal wedge' },
  },
  quadrants: {
    // v11 §2K: the cross-cut players expected Direction to be.
    how: 'Tells you which map quadrant the hider is in (the four regions made by a north-south and an east-west line crossing at you) and clips the possible area to that quadrant. The edges run along the map grid (the cross), unlike Direction’s diagonal X.',
    mechanics: { cost: cost('quadrants'), window: vetoWin, mapImpact: 'Clips the zone to one N/S–E/W quadrant (cross-cut)' },
    demo: { type: 'quadrants', caption: 'Which map quadrant? → zone clipped to the cross-cut' },
  },
  borders: {
    // v11 §2L: the edge-hiding counter — a binary region-match risk card.
    how: 'Splits the play area into its OUTER BAND (the ring along the boundary holding 25% of the area) and its INNER CORE (the 75% middle), then tells you which one the hider is in and clips the zone to it. An edge-hugging hider gives up the whole core (the jackpot); a centered hider only costs you the band. Cheap, and spicy against the edge-hiding meta.',
    mechanics: { cost: cost('borders'), window: vetoWin, mapImpact: 'Clips the zone to the outer 25% band or the inner 75% core' },
    demo: { type: 'borders', caption: 'Band or core? → an edge-hider forfeits the middle' },
  },
  // ---- v24 BOLT-118..122: LANDMARK cards. The first cards that cut by REAL
  // map features instead of pure geometry. Every one works the same way, which
  // is the point: the pipeline is the investment, a new card is a tag + copy.
  nearestPark: {
    how: 'Divides the play area by which park is closest, then tells you which park the hider is nearest and clips the zone to that park\'s region. If the park has a name on the map, you get the name, which is a real clue you can walk toward. Strength depends on the map: lots of parks means a tight cut, one park means the card is dealt disabled.',
    mechanics: { cost: cost('nearestPark'), window: vetoWin, mapImpact: "Clips the zone to the region closest to one park" },
    demo: { type: 'landmark', caption: 'Which park is nearest? → keep only that park\'s region' },
  },
  nearestRestaurant: {
    how: 'The same cut, by places to eat: restaurants, fast food and cafes all count as one class, and each individual place is its own region. Dense in a town centre, so it can cut hard there and barely at all on a quiet residential block.',
    mechanics: { cost: cost('nearestRestaurant'), window: vetoWin, mapImpact: 'Clips the zone to the region closest to one place to eat' },
    demo: { type: 'landmark', caption: 'Which place to eat is nearest? → keep only that region' },
  },
  nearestPlayground: {
    how: 'The same cut, by playgrounds. Sparser than parks, so when it lands it usually lands hard, but on many maps it will be dealt disabled for want of a second playground.',
    mechanics: { cost: cost('nearestPlayground'), window: vetoWin, mapImpact: 'Clips the zone to the region closest to one playground' },
    demo: { type: 'landmark', caption: 'Which playground is nearest? → keep only that region' },
  },
  nearestSchool: {
    how: 'The same cut, by schools. School grounds are large, so their regions tend to be large and clean, which makes this a dependable mid-strength cut in a residential area.',
    mechanics: { cost: cost('nearestSchool'), window: vetoWin, mapImpact: 'Clips the zone to the region closest to one school' },
    demo: { type: 'landmark', caption: 'Which school is nearest? → keep only that region' },
  },
  nearestWater: {
    how: 'The same cut, by water: lakes and ponds are areas, rivers and streams are lines, and all of them count as one class with each body its own region. Standing IN or ON a water feature counts as zero distance, so a hider by the creek cannot dodge it.',
    mechanics: { cost: cost('nearestWater'), window: vetoWin, mapImpact: 'Clips the zone to the region closest to one body of water' },
    demo: { type: 'landmark', caption: 'Which water is nearest? → keep only that region' },
  },
  nearestBridge: {
    how: 'The same cut, by bridges. Bridges are lines rather than areas, and they are usually few and far apart, so this is the sharpest of the set on a map that has them and absent on a map that does not.',
    mechanics: { cost: cost('nearestBridge'), window: vetoWin, mapImpact: 'Clips the zone to the region closest to one bridge' },
    demo: { type: 'landmark', caption: 'Which bridge is nearest? → keep only that region' },
  },
  // Slice — v21 BOLT-101 (the cut is along the walk now; wire id 'thermo250'
  // + family 'thermometer' stay frozen). v14 BOLT-044: one 250 ft distance,
  // multiple identical plays.
  thermo250: {
    how: `Starts a timed walk (${thermoWin}). Walk at least ${THERMO_MINS_FT[0]} ft from where you started; the app draws a live line along the path you walk. When you stop (or hit the minimum) it slices the map along that line and clips the zone to the side the hider is on. Walk less than the minimum before time runs out and it fails: no clip. You hold several copies; each is the same ${THERMO_MINS_FT[0]} ft ask.`,
    mechanics: { cost: `${cost('thermo250')} (on success)`, window: `${thermoWin} walk window`, mapImpact: "Slices the zone along your path, keeping the hider's side" },
    demo: { type: 'thermometer', param: THERMO_MINS_FT[0], caption: `Walk ${THERMO_MINS_FT[0]} ft → slice along your path → keep the hider's side` },
  },
  // Photo family
  photoUp: {
    how: `Make the hider prove it. The hider gets ${photoWin} to upload a photo of the named subject (here: straight up). The image is stripped of location data and shared to both teams; it does not reveal coordinates. It is pressure and a clue. If the hider fails to upload in time, a penalty zone is revealed: the zone clips to a ${penaltyFt} circle around the hider (you learn "roughly here," never the exact point).`,
    mechanics: { cost: `${cost('photoUp')} (on upload)`, window: `${photoWin} upload window`, mapImpact: `No clip on success · fail → ${penaltyFt} penalty zone` },
    demo: { type: 'photo', param: 'cloud', caption: 'Camera → a shot of the sky, straight up' }, // v15 BOLT-056
  },
  photoMailbox: {
    how: `The hider gets ${photoWin} to photograph the nearest mailbox, which often forces them to step out toward the street. Same rules as the other photo cards: a clue on success, a ${penaltyFt} penalty zone on a timeout.`,
    mechanics: { cost: `${cost('photoMailbox')} (on upload)`, window: `${photoWin} upload window`, mapImpact: `No clip on success · fail → ${penaltyFt} penalty zone` },
    demo: { type: 'photo', param: 'mailbox', caption: 'Camera → the nearest mailbox' }, // v15 BOLT-056
  },
  photoHouse: {
    how: `The hider gets ${photoWin} to photograph the closest house. A clue on success; a ${penaltyFt} penalty zone if they cannot deliver in time.`,
    mechanics: { cost: `${cost('photoHouse')} (on upload)`, window: `${photoWin} upload window`, mapImpact: `No clip on success · fail → ${penaltyFt} penalty zone` },
    demo: { type: 'photo', param: 'house', caption: 'Camera → the closest house' }, // v15 BOLT-056
  },
  photoFeet: {
    how: `The hider gets ${photoWin} to photograph their own feet: proof of where they are standing. A clue on success; a ${penaltyFt} penalty zone on a timeout.`,
    mechanics: { cost: `${cost('photoFeet')} (on upload)`, window: `${photoWin} upload window`, mapImpact: `No clip on success · fail → ${penaltyFt} penalty zone` },
    demo: { type: 'photo', param: 'feet', caption: 'Camera → their feet' }, // v15 BOLT-056
  },
  photoBuilding: {
    how: `The hider gets ${photoWin} to photograph the tallest building in view, a landmark that can give away where they are. A clue on success; a ${penaltyFt} penalty zone if they time out.`,
    mechanics: { cost: `${cost('photoBuilding')} (on upload)`, window: `${photoWin} upload window`, mapImpact: `No clip on success · fail → ${penaltyFt} penalty zone` },
    demo: { type: 'photo', param: 'building', caption: 'Camera → the tallest building in view' }, // v15 BOLT-056
  },
  photoSelfie: {
    how: `The hider gets ${photoWin} to take a SELFIE: the front camera opens, and whatever is behind them ships with the smile. The background is the clue. Same rules as every photo card: a clue on success, a ${penaltyFt} penalty zone on a timeout.`,
    mechanics: { cost: `${cost('photoSelfie')} (on upload)`, window: `${photoWin} upload window`, mapImpact: `No clip on success · fail → ${penaltyFt} penalty zone` },
    demo: { type: 'photo', param: 'person', caption: 'Front camera → you (the background is the clue)' }, // v15 BOLT-056
  },
  airhorn: {
    // v16 BOLT-072 — EXPERIMENTAL (field trial). v6 rooms only. v17 BOLT-076
    // moved the volume gate to CAST time: always dealt, and a horn that does
    // not confirm playback refunds the seeker.
    how: `Sounds a piercing whistle on the hider's phone, for when you KNOW they are close (tall grass, a hedge, a parked car) but cannot see them. The hider also sees a WHISTLE! banner so they know what that noise was. If it does not play out loud within a few seconds, you get the card back and the hider draws nothing.`,
    mechanics: { cost: `${cost('airhorn')} (on delivery; a whistle that comes back deals nothing)`, mapImpact: 'No map effect. You localize by ear' },
    demo: { type: 'airhorn', caption: 'Cast → the hider\'s phone shrieks, or the card comes back' },
  },
};
